import PictureImage from '../Common/PictureImage';
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useInViewOnce } from "../../hooks/useInViewOnce";
import Footer from "../Layouts/Footer";
import {
  FORMSPREE_CONTACT_FORM,
  readFormspreeErrorMessage,
  submitFormspreeNativePost,
} from "../../utils/formspree";
import { trackLeadSubmission } from "../../utils/trackLead";
import "./layout.css";
import { absoluteUrl } from "../../seo/siteOrigin";

// ─── Types ───────────────────────────────────────────────────────────────────
interface ServiceCard {
  icon: string;
  title: string;
  desc: ReactNode;
}
interface Step {
  num: string;
  title: string;
  desc: ReactNode;
}
interface Logo {
  name: string;
  abbr: string;
}
interface FormState {
  name: string;
  email: string;
  volume: string;
}

const FONT_STACK =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif";
const PAGE_BG = "#ffffff";
const PAGE_ALT = "#f8fafc";
const PAGE_BORDER = "#e2e8f0";
const TEXT_DARK = "#0f172a";
const TEXT_MID = "#334155";
const TEXT_SOFT = "#64748b";
const ACCENT = "#f97316";
const ACCENT_RGB = "249, 115, 22";

const inlineLinkOnDark: React.CSSProperties = {
  color: "#fbbf24",
  textDecoration: "underline",
  textUnderlineOffset: 3,
};

const inlineLinkOnLight: React.CSSProperties = {
  color: ACCENT,
  textDecoration: "underline",
  textUnderlineOffset: 3,
};

// ─── Data ────────────────────────────────────────────────────────────────────
const SERVICES: ServiceCard[] = [
  {
    icon: "🏗️",
    title: "Bulk Receiving & Unloading",
    desc: (
      <>
        Full truckloads, pallets, and containers processed with same-day inventory receiving at our{' '}
        <Link to="/amazon-fba-prep-centre-services" style={inlineLinkOnLight}>Milton Keynes FBA prep facility</Link>.
      </>
    ),
  },
  {
    icon: "🏷️",
    title: "FNSKU Labelling at Scale",
    desc: (
      <>
        High-speed <Link to="/blog/fnsku-labelling-service-uk" style={inlineLinkOnLight}>FNSKU labelling</Link> with barcode verification and inventory accuracy.
      </>
    ),
  },
  {
    icon: "📅",
    title: "Expiry Date Checking",
    desc: "Every unit inspected for expiry compliance. Non-compliant stock is quarantined and reported immediately.",
  },
  {
    icon: "📬",
    title: "Amazon Compliant Shipments",
    desc: (
      <>
        <Link to="/amazon-fba-shipments" style={inlineLinkOnLight}>Amazon FBA shipment</Link> plans, pallet labels, and box content lists built accurately.
      </>
    ),
  },
];

const STEPS: Step[] = [
  { num: "01", title: "Submit Inventory", desc: "Send your purchase order and shipment details before inventory arrives." },
  { num: "02", title: "We Receive & Check", desc: "Inventory received, counted, inspected, and logged into stock." },
  {
    num: "03",
    title: "Prep & Label",
    desc: (
      <>
        <Link to="/blog/fnsku-labelling-service-uk" style={inlineLinkOnLight}>FNSKU labelling</Link>,{" "}
        <Link to="/blog/amazon-poly-bag-requirements-uk" style={inlineLinkOnLight}>poly bagging</Link>, and bundling completed to your specifications.
      </>
    ),
  },
  {
    num: "04",
    title: "Ship to Amazon",
    desc: (
      <>
        Compliant <Link to="/amazon-fba-shipments" style={inlineLinkOnLight}>FBA shipments</Link> tracked, delivered, and confirmed by Amazon.
      </>
    ),
  },
];

const LOGOS: Logo[] = [
  { name: "BrandCo", abbr: "BC" },
  { name: "DistroHub", abbr: "DH" },
  { name: "ScaleUp", abbr: "SU" },
  { name: "PrimeGoods", abbr: "PG" },
  { name: "VaultSell", abbr: "VS" },
  { name: "StackedCo", abbr: "SC" },
];

const MARQUEE_ITEMS = [
  "Bulk Receiving",
  "FNSKU Labelling",
  "Expiry Checking",
  "Poly Bagging",
  "Bubble Wrapping",
  "Bundle Creation",
  "Kitting",
  "Pallet Building",
  "Box Content Lists",
  "Shipment Plans",
  "Quality Control",
  "Returns Processing",
];

const VOLUME_LABELS: Record<string, string> = {
  "": "",
  "500-2000": "500 – 2,000 units/month",
  "2000-6000": "2,000 – 6,000 units/month",
  "6000-15000": "6,000 – 15,000 units/month",
  "15000+": "15,000+ units/month",
};

// ─── Counter Hook ─────────────────────────────────────────────────────────────
function useCounter(target: number, duration: number, trigger: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let current = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, 16);
    return () => clearInterval(timer);
  }, [trigger, target, duration]);
  return count;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function WholesaleFBAPrep() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormState>({ name: "", email: "", volume: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [hoveredLogo, setHoveredLogo] = useState<number | null>(null);

  const counterRef = useRef<HTMLDivElement>(null);
  const counterInView = useInViewOnce(counterRef, { once: true, margin: "-80px" });
  const stepsRef = useRef<HTMLDivElement>(null);
  const stepsInView = useInViewOnce(stepsRef, { once: true, margin: "-80px" });
  const servicesRef = useRef<HTMLDivElement>(null);
  const servicesInView = useInViewOnce(servicesRef, { once: true, margin: "-80px" });

  const unitCount = useCounter(10000, 2200, counterInView);

  const handleWholesaleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.volume) return;

    setIsSubmitting(true);
    setSubmitError("");

    const volLabel = VOLUME_LABELS[formData.volume] || formData.volume;
    const subject = `Wholesale FBA rate request — ${formData.name.trim()}`;
    const messageBody = [
      "Wholesale FBA prep — rate request (pickpackpro.co.uk/amazon-fba-prep-wholesale)",
      `Name: ${formData.name.trim()}`,
      `Email: ${formData.email.trim()}`,
      `Monthly volume: ${volLabel}`,
    ].join("\n");

    const thankYouUrl = absoluteUrl('/thank-you');
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 60000);

    try {
      const fd = new FormData();
      fd.append("name", formData.name.trim());
      fd.append("email", formData.email.trim());
      fd.append("monthlyVolume", volLabel);
      fd.append("monthlyVolumeValue", formData.volume);
      fd.append("message", messageBody);
      fd.append("formSource", "wholesale-fba-prep");
      fd.append("_replyto", formData.email.trim());
      fd.append("_subject", subject);
      fd.append("_next", thankYouUrl);

      const response = await fetch(FORMSPREE_CONTACT_FORM, {
        method: "POST",
        body: fd,
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });

      if (response.ok) {
        trackLeadSubmission({
          source: 'wholesale-fba-prep',
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: messageBody,
          extra: { monthlyVolume: volLabel, monthlyVolumeValue: formData.volume },
        });
        setIsSubmitting(false);
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        navigate("/thank-you");
        return;
      }
      setSubmitError(await readFormspreeErrorMessage(response));
      setIsSubmitting(false);
    } catch (err) {
      const aborted = err instanceof DOMException && err.name === "AbortError";
      if (aborted) {
        setSubmitError("Request timed out. Please try again or email info@pickpackpro.co.uk.");
        setIsSubmitting(false);
      } else {
        try {
          submitFormspreeNativePost(
            FORMSPREE_CONTACT_FORM,
            [
              ["name", formData.name.trim()],
              ["email", formData.email.trim()],
              ["monthlyVolume", volLabel],
              ["monthlyVolumeValue", formData.volume],
              ["message", messageBody],
              ["formSource", "wholesale-fba-prep"],
              ["_replyto", formData.email.trim()],
              ["_subject", subject],
            ],
            thankYouUrl
          );
        } catch {
          setSubmitError(
            "We could not send your request from this browser. Please email info@pickpackpro.co.uk or try again."
          );
          setIsSubmitting(false);
        }
      }
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  return (
    <div className="wholesale-fba-prep-page" style={{ minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        .wholesale-fba-prep-page *, .wholesale-fba-prep-page *::before, .wholesale-fba-prep-page *::after { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: ${ACCENT}; border-radius: 3px; }

        @keyframes marqueeScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes lineGrow {
          from { width: 0; }
          to { width: 100%; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(244,165,36,0.4); }
          50% { box-shadow: 0 0 36px rgba(244,165,36,0.75), 0 0 60px rgba(244,165,36,0.2); }
        }
        @keyframes stepLineAnim {
          from { width: 0%; }
          to { width: 100%; }
        }

        .svc-card:hover {
          border-color: rgba(${ACCENT_RGB}, 0.45) !important;
          transform: translateY(-5px);
          box-shadow: 0 14px 32px rgba(15,23,42,0.08);
        }
        .svc-card:hover .svc-icon {
          background: ${ACCENT} !important;
          color: #fff !important;
        }
        .submit-btn:hover:not(:disabled) {
          background: #ea580c !important;
        }
        .logo-pill:hover {
          border-color: rgba(${ACCENT_RGB}, 0.45) !important;
          box-shadow: 0 6px 20px rgba(15,23,42,0.08) !important;
        }

        @media (max-width: 900px) {
          .services-grid { grid-template-columns: 1fr 1fr !important; }
          .steps-row { flex-direction: column !important; }
          .step-connector { display: none !important; }
          .split-layout { flex-direction: column !important; }
          .split-left, .split-right { width: 100% !important; }
          .logos-row { gap: 12px !important; }
        }
        @media (max-width: 560px) {
          .services-grid { grid-template-columns: 1fr !important; }
          .counter-num { font-size: 4rem !important; }
        }
      `}</style>

      {/* ══════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════ */}
      <section className="hero-section fba-page-hero" style={{ textAlign: "left" }}>
        <PictureImage
          className="fba-page-hero__bg"
          src="/amazonfba/wholesalefba.webp"
          alt="Wholesale Amazon FBA prep and bulk inventory processing UK"
          width={1600}
          height={900}
          decoding="async"
          isHero
        />
        <div className="fba-page-hero__overlay" aria-hidden />

        <div className="fba-page-hero__content">
          <div style={{ maxWidth: "min(860px, 100%)" }}>
          {/* Tag line */}
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ marginBottom: 24 }}
          >
            <span style={{
              display: "inline-block",
              border: "1px solid rgba(244,165,36,0.5)",
              color: "#f4a524",
              padding: "5px 18px",
              borderRadius: 3,
              fontSize: "0.78rem",
              fontFamily: "Arial, sans-serif",
              letterSpacing: "0.18em",
             
            }}>
              Wholesale FBA Prep UK
            </span>
          </motion.div>

          {/* Main title */}
          <motion.h1
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              lineHeight: 1.15,
              color: "#fff",
              marginBottom: 32,
              fontWeight: 700,
            }}
          >
            Wholesale FBA Prep UK —{' '}
            <span style={{ position: "relative", display: "inline-block", color: '#f97316' }}>
              Built for Volume
              <motion.span
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 0.9, delay: 1.2, ease: "easeOut" }}
                style={{
                  position: "absolute",
                  bottom: -5, left: 0, height: 3,
                  background: "#f4a524",
                  borderRadius: 2,
                  display: "block",
                }}
              />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            style={{
              fontFamily: "Arial, sans-serif",
              color: "rgba(255,255,255,0.6)",
              lineHeight: 1.75,
              maxWidth: 580,
              margin: "0 0 44px",
            }}
          >
            High-volume prep services for wholesalers, distributors, and Amazon sellers. We process thousands of units daily with speed, accuracy, and consistent compliance — part of our{' '}
            <Link to="/amazon-fba-prep-services" style={inlineLinkOnDark}>Amazon FBA prep service UK</Link> with{' '}
            <Link to="/amazon-fba-prep-pricing" style={inlineLinkOnDark}>transparent FBA prep pricing</Link> and{' '}
            <Link to="/amazon-fba-prep-centre-services" style={inlineLinkOnDark}>Milton Keynes facility capacity</Link> for bulk receiving.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.05 }}
            style={{ display: "flex", gap: 14, justifyContent: "flex-start", flexWrap: "wrap" }}
          >
            <button
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              style={{
                background: '#f97316',
                color: "#1a1a1a",
                border: "none",
                padding: "14px 36px",
                borderRadius: 4,
                fontSize: "0.95rem",
                fontFamily: "Arial, sans-serif",
                fontWeight: 700,
                cursor: "pointer",
                letterSpacing: "0.04em",
                animation: "glowPulse 3.5s ease infinite",
                transition: "background 0.2s",
              }}
            >
              Get Wholesale Pricing
            </button>
            <button
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              style={{
                background: "transparent",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                padding: "14px 36px",
                borderRadius: 4,
                fontSize: "0.95rem",
                fontFamily: "Arial, sans-serif",
                cursor: "pointer",
                letterSpacing: "0.04em",
                transition: "border-color 0.2s",
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = "#f4a524")}
              onMouseLeave={e => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)")}
            >
              View Services ↓
            </button>
          </motion.div>

          {/* Trust bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.7 }}
            style={{
              marginTop: 64,
              paddingTop: 36,
              borderTop: "1px solid rgba(255,255,255,0.08)",
              display: "flex", justifyContent: "flex-start", gap: 48, flexWrap: "wrap",
            }}
          >
            {[["10,000+", "Units/Month"], ["99.9%", "Accuracy"], ["24hr", "Max Turnaround"], ["250+", "Clients"]].map(([val, lbl]) => (
              <div key={lbl} style={{ textAlign: "left" }}>
                <div style={{ fontFamily: "Georgia, serif", fontSize: "1.5rem", color: '#f97316', fontWeight: 700 }}>{val}</div>
                <div style={{ fontFamily: "Arial, sans-serif", fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", letterSpacing: "0.12em",  marginTop: 4 }}>{lbl}</div>
              </div>
            ))}
          </motion.div>
          </div>
        </div>
      </section>

      <div
        style={{
          background: PAGE_BG,
          color: TEXT_DARK,
          fontFamily: FONT_STACK,
        }}
      >
      {/* ══════════════════════════════════════════════════════
          MARQUEE STRIP
      ══════════════════════════════════════════════════════ */}
      <div style={{
        background: PAGE_ALT,
        borderTop: `1px solid ${PAGE_BORDER}`,
        borderBottom: `1px solid ${PAGE_BORDER}`,
        padding: "14px 0",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}>
        <motion.div style={{ display: "inline-flex", animation: "marqueeScroll 28s linear infinite" }}>
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} style={{
              display: "inline-flex", alignItems: "center",
              fontFamily: FONT_STACK,
              fontSize: "0.78rem",
              letterSpacing: "0.18em",
              fontWeight: 600,
              color: i % 4 === 0 ? ACCENT : TEXT_SOFT,
              marginRight: 0,
              padding: "0 28px",
            }}>
              {item}
              <span style={{ marginLeft: 28, color: "rgba(249,115,22,0.35)", fontSize: "0.5rem" }}>◆</span>
            </span>
          ))}
        </motion.div>
      </div>

      {/* ══════════════════════════════════════════════════════
          SERVICES
      ══════════════════════════════════════════════════════ */}
      <section id="services" style={{ padding: "100px 24px", background: PAGE_BG }}>
        <div style={{ maxWidth: 1140, margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: "center", marginBottom: 64 }}
          >
            <p style={{ fontFamily: FONT_STACK, fontSize: "0.72rem", color: ACCENT, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 14, fontWeight: 700 }}>
              What We Do
            </p>
            <h2 style={{ fontFamily: FONT_STACK, fontSize: "clamp(1.9rem, 4vw, 2.9rem)", color: TEXT_DARK, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-0.02em" }}>
              Core Prep Services
            </h2>
            <p style={{ fontFamily: FONT_STACK, color: TEXT_SOFT, marginTop: 14, fontSize: "0.95rem", maxWidth: 520, margin: "14px auto 0", lineHeight: 1.7 }}>
              Everything your wholesale FBA operation needs managed under one roof — compare{" "}
              <Link to="/amazon-fba-prep-pricing" style={inlineLinkOnLight}>FBA prep pricing UK</Link> and our{" "}
              <Link to="/amazon-fba-prep-private-label" style={inlineLinkOnLight}>private label FBA prep</Link> if you run mixed inventory.
            </p>
          </motion.div>

          <div ref={servicesRef} className="services-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20 }}>
            {SERVICES.map((svc, i) => (
              <motion.div
                key={svc.title}
                className="svc-card"
                initial={{ opacity: 0, y: 40 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  background: PAGE_BG,
                  border: `1px solid ${PAGE_BORDER}`,
                  borderRadius: 12,
                  padding: "32px 24px",
                  transition: "all 0.3s ease",
                  cursor: "default",
                  boxShadow: "0 4px 20px rgba(15,23,42,0.05)",
                }}
              >
                <div
                  className="svc-icon"
                  style={{
                    width: 52, height: 52,
                    borderRadius: 8,
                    background: "rgba(249,115,22,0.1)",
                    border: `1px solid rgba(${ACCENT_RGB}, 0.25)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "1.6rem",
                    marginBottom: 22,
                    transition: "all 0.3s ease",
                  }}
                >
                  {svc.icon}
                </div>
                <h3 style={{ fontFamily: FONT_STACK, fontSize: "1rem", color: TEXT_DARK, fontWeight: 700, marginBottom: 12, lineHeight: 1.3 }}>
                  {svc.title}
                </h3>
                <p style={{ fontFamily: FONT_STACK, fontSize: "0.875rem", color: TEXT_MID, lineHeight: 1.65 }}>
                  {svc.desc}
                </p>
                <div style={{ marginTop: 20, height: 2, width: 32, background: ACCENT, borderRadius: 1 }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════════════════ */}
      <section id="process" style={{ padding: "100px 24px", background: PAGE_ALT, position: "relative", borderTop: `1px solid ${PAGE_BORDER}` }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent, rgba(244,165,36,0.25), transparent)" }} />

        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ textAlign: "center", marginBottom: 72 }}
          >
            <p style={{ fontFamily: FONT_STACK, fontSize: "0.72rem", color: ACCENT, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 14, fontWeight: 700 }}>
              How It Works
            </p>
            <h2 style={{ fontFamily: FONT_STACK, fontSize: "clamp(1.9rem, 4vw, 2.9rem)", color: TEXT_DARK, fontWeight: 800, letterSpacing: "-0.02em" }}>
              Simple. Fast. Reliable.
            </h2>
            <p style={{ fontFamily: FONT_STACK, color: TEXT_SOFT, marginTop: 14, fontSize: "0.95rem", maxWidth: 560, margin: "14px auto 0", lineHeight: 1.7 }}>
              Follow our{" "}
              <Link to="/blog/how-to-prep-products-for-amazon-fba-uk" style={inlineLinkOnLight}>step-by-step FBA prep UK guide</Link> or plan ahead for{" "}
              <Link to="/blog/amazon-ending-fba-prep-services-uk-2026" style={inlineLinkOnLight}>July 2026 Amazon prep changes</Link>.
            </p>
          </motion.div>

          {/* Steps row */}
          <div ref={stepsRef} className="steps-row" style={{ display: "flex", alignItems: "flex-start", position: "relative" }}>
            {/* Animated connecting line (desktop) */}
            <div style={{
              position: "absolute", top: 32, left: "6%", right: "6%", height: 2,
              background: PAGE_BORDER,
              zIndex: 0,
            }}>
              {stepsInView && (
                <div style={{
                  height: "100%",
                  background: `linear-gradient(90deg, ${ACCENT}, rgba(${ACCENT_RGB}, 0.25))`,
                  animation: "stepLineAnim 1.5s ease 0.3s both forwards",
                  width: 0,
                }} />
              )}
            </div>

            {STEPS.map((step, i) => (
              <motion.div
                key={step.num}
                initial={{ opacity: 0, y: 32 }}
                animate={stepsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.55, delay: i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                style={{ flex: 1, textAlign: "center", padding: "0 16px", position: "relative", zIndex: 1 }}
              >
                {/* Number circle */}
                <div style={{
                  width: 64, height: 64,
                  borderRadius: "50%",
                  background: i === 0 ? ACCENT : PAGE_BG,
                  border: i === 0 ? "none" : `2px solid rgba(${ACCENT_RGB}, 0.35)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 22px",
                  transition: "all 0.3s",
                  boxShadow: i === 0 ? `0 8px 24px rgba(${ACCENT_RGB}, 0.35)` : "0 2px 8px rgba(15,23,42,0.06)",
                }}>
                  <span style={{
                    fontFamily: FONT_STACK,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    color: i === 0 ? "#fff" : ACCENT,
                  }}>{step.num}</span>
                </div>
                <h3 style={{ fontFamily: FONT_STACK, fontSize: "0.98rem", color: TEXT_DARK, fontWeight: 700, marginBottom: 10 }}>
                  {step.title}
                </h3>
                <p style={{ fontFamily: FONT_STACK, fontSize: "0.875rem", color: TEXT_SOFT, lineHeight: 1.6 }}>
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          VOLUME COUNTER
      ══════════════════════════════════════════════════════ */}
      <section id="pricing" style={{ padding: "100px 24px", background: PAGE_BG, position: "relative", overflow: "hidden", borderTop: `1px solid ${PAGE_BORDER}` }}>
        <div style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
          width: 700, height: 700,
          borderRadius: "50%",
          background: `radial-gradient(circle, rgba(${ACCENT_RGB}, 0.06) 0%, transparent 70%)`,
          pointerEvents: "none",
        }} />

        <div ref={counterRef} style={{ maxWidth: 880, margin: "0 auto", textAlign: "center", position: "relative" }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p style={{ fontFamily: FONT_STACK, fontSize: "0.72rem", color: ACCENT, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 14, fontWeight: 700 }}>
              Volume Pricing
            </p>
            <h2 style={{ fontFamily: FONT_STACK, fontSize: "clamp(1.9rem, 4vw, 2.9rem)", color: TEXT_DARK, fontWeight: 800, marginBottom: 16, letterSpacing: "-0.02em" }}>
              Higher Volumes Mean Lower Per-Unit Costs
            </h2>
            <p style={{ fontFamily: FONT_STACK, color: TEXT_SOFT, fontSize: "0.95rem", maxWidth: 560, margin: "0 auto 48px", lineHeight: 1.7 }}>
              Volume tiers sit alongside transparent{" "}
              <Link to="/pricing-fulfilment-services" style={inlineLinkOnLight}>fulfilment pricing</Link> — request a tailored rate card via{" "}
              <Link to="/contact-fulfillment-services" style={inlineLinkOnLight}>contact</Link> if you ship 15,000+ units monthly.
            </p>
          </motion.div>

          {/* Counter display */}
          <div style={{
            display: "inline-flex", flexDirection: "column", alignItems: "center",
            background: `rgba(${ACCENT_RGB}, 0.08)`,
            border: `1px solid rgba(${ACCENT_RGB}, 0.25)`,
            borderRadius: 12,
            padding: "48px 72px",
            marginBottom: 64,
          }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span className="counter-num" style={{
                fontFamily: FONT_STACK,
                fontSize: "clamp(3.5rem, 8vw, 5.5rem)",
                color: ACCENT,
                fontWeight: 800,
                lineHeight: 1,
              }}>
                {unitCount.toLocaleString()}
              </span>
              <span style={{ fontFamily: FONT_STACK, fontSize: "2.5rem", color: ACCENT, fontWeight: 800 }}>+</span>
            </div>
            <div style={{ fontFamily: FONT_STACK, fontSize: "0.8rem", color: TEXT_SOFT, letterSpacing: "0.2em", marginTop: 12, textTransform: "uppercase", fontWeight: 600 }}>
              Wholesale Units Processed Monthly
            </div>
          </div>

          {/* Tier table */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
            {[
              { tier: "Starter", units: "500–2,000 Units Monthly", benefit: "Standard Per-Unit Rates Apply" },
              { tier: "Growth", units: "2,000–6,000 Units Monthly", benefit: "10% Volume Discount Applied" },
              { tier: "Wholesale", units: "6,000+ Units Monthly", benefit: "Custom Pricing & Priority Processing" },
            ].map((tier, i) => (
              <motion.div
                key={tier.tier}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                style={{
                  background: i === 2 ? `rgba(${ACCENT_RGB}, 0.1)` : PAGE_ALT,
                  border: i === 2 ? `1px solid rgba(${ACCENT_RGB}, 0.45)` : `1px solid ${PAGE_BORDER}`,
                  borderRadius: 12,
                  boxShadow: i === 2 ? `0 8px 24px rgba(${ACCENT_RGB}, 0.12)` : "none",
                  padding: "24px 20px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontFamily: FONT_STACK, fontSize: "0.68rem", color: ACCENT, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 10, fontWeight: 700 }}>{tier.tier}</div>
                <div style={{ fontFamily: FONT_STACK, fontSize: "0.95rem", color: TEXT_DARK, fontWeight: 700, marginBottom: 8 }}>{tier.units}</div>
                <div style={{ fontFamily: "Arial, sans-serif", fontSize: "0.875rem", color: TEXT_SOFT }}>{tier.benefit}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          CLIENT LOGOS
      ══════════════════════════════════════════════════════ */}
      <section style={{ padding: "80px 24px", background: PAGE_ALT, borderTop: `1px solid ${PAGE_BORDER}` }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            style={{ fontFamily: FONT_STACK, fontSize: "0.72rem", color: TEXT_SOFT, letterSpacing: "0.25em", textTransform: "uppercase", textAlign: "center", marginBottom: 36, fontWeight: 600 }}
          >
            Trusted by Wholesale Sellers
          </motion.p>
          <div className="logos-row" style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            {LOGOS.map((logo, i) => (
              <motion.div
                key={logo.name}
                className="logo-pill"
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                onMouseEnter={() => setHoveredLogo(i)}
                onMouseLeave={() => setHoveredLogo(null)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 24px",
                  border: `1px solid ${PAGE_BORDER}`,
                  borderRadius: 8,
                  cursor: "default",
                  transition: "all 0.3s ease",
                  background: PAGE_BG,
                  filter: hoveredLogo === i ? "none" : "none",
                  color: hoveredLogo === i ? ACCENT : TEXT_MID,
                }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: 4,
                  background: hoveredLogo === i ? `rgba(${ACCENT_RGB}, 0.15)` : PAGE_ALT,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: FONT_STACK, fontWeight: 700, fontSize: "0.72rem",
                  color: hoveredLogo === i ? ACCENT : TEXT_MID,
                  transition: "background 0.3s",
                }}>
                  {logo.abbr}
                </div>
                <span style={{ fontFamily: FONT_STACK, fontSize: "0.9rem", fontWeight: 700, color: TEXT_DARK }}>{logo.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          SPLIT: TESTIMONIAL + FORM
      ══════════════════════════════════════════════════════ */}
      <section id="contact" style={{ padding: "100px 24px", background: PAGE_BG, borderTop: `1px solid ${PAGE_BORDER}` }}>
        <div style={{ position: "absolute", left: 0, right: 0, height: 1, background: "linear-gradient(90deg, transparent, rgba(244,165,36,0.2), transparent)", marginTop: -100 }} />
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div className="split-layout" style={{ display: "flex", gap: 48, alignItems: "stretch" }}>

            {/* LEFT: Testimonial */}
            <motion.div
              className="split-left"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              style={{
                width: "50%",
                background: PAGE_ALT,
                border: `1px solid ${PAGE_BORDER}`,
                borderRadius: 12,
                padding: "48px 40px",
                display: "flex", flexDirection: "column", justifyContent: "space-between",
                position: "relative", overflow: "hidden",
                boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
              }}
            >
              {/* Quote mark */}
              <div style={{
                position: "absolute", top: 20, right: 28,
                fontFamily: FONT_STACK,
                fontSize: "8rem", color: `rgba(${ACCENT_RGB}, 0.12)`,
                lineHeight: 1, userSelect: "none",
              }}>"</div>

              <div>
                <div style={{ display: "flex", gap: 4, marginBottom: 28 }}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ color: ACCENT, fontSize: "1.1rem" }}>★</span>
                  ))}
                </div>
                <p style={{
                  fontFamily: FONT_STACK,
                  fontSize: "1.1rem",
                  color: TEXT_MID,
                  lineHeight: 1.75,
                  fontStyle: "italic",
                  marginBottom: 36,
                }}>
                  "We ship 4,000 units a month to this prep centre and have not had a single removal order from Amazon in over 14 months. The accuracy, speed and communication are genuinely exceptional for a wholesale operation at our scale."
                </p>
              </div>

              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 36 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: "50%",
                    background: `linear-gradient(135deg, ${ACCENT}, #ea580c)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontFamily: FONT_STACK, fontWeight: 700, color: "#fff", fontSize: "1rem",
                  }}>JM</div>
                  <div>
                    <div style={{ fontFamily: FONT_STACK, fontSize: "0.95rem", color: TEXT_DARK, fontWeight: 700 }}>James Morrison</div>
                    <div style={{ fontFamily: FONT_STACK, fontSize: "0.78rem", color: TEXT_SOFT, marginTop: 3 }}>Wholesale FBA Seller · 4,000 units/mo</div>
                  </div>
                </div>

                {/* Mini stats */}
                <div style={{ display: "flex", gap: 24, paddingTop: 28, borderTop: `1px solid ${PAGE_BORDER}` }}>
                  {[["14 months", "Zero removals"], ["4,000/mo", "Units shipped"], ["48hr", "Avg prep time"]].map(([val, lbl]) => (
                    <div key={lbl}>
                      <div style={{ fontFamily: FONT_STACK, fontSize: "1rem", color: ACCENT, fontWeight: 700 }}>{val}</div>
                      <div style={{ fontFamily: FONT_STACK, fontSize: "0.72rem", color: TEXT_SOFT, marginTop: 3 }}>{lbl}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* RIGHT: Form */}
            <motion.div
              className="split-right"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              style={{
                width: "50%",
                background: PAGE_BG,
                border: `1px solid rgba(${ACCENT_RGB}, 0.35)`,
                borderRadius: 12,
                padding: "48px 40px",
                boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
              }}
            >
              <p style={{ fontFamily: FONT_STACK, fontSize: "0.7rem", color: ACCENT, letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 12, fontWeight: 700 }}>
                Get a Quote
              </p>
              <h3 style={{ fontFamily: FONT_STACK, fontSize: "1.7rem", color: TEXT_DARK, fontWeight: 800, marginBottom: 8, lineHeight: 1.2 }}>
                Get Wholesale Pricing
              </h3>
              <p style={{ fontFamily: FONT_STACK, fontSize: "0.9rem", color: TEXT_SOFT, marginBottom: 36, lineHeight: 1.6 }}>
                Tell us your monthly volume and requirements, and we'll provide a custom wholesale rate card within two business hours. See full{" "}
                <Link to="/amazon-fba-prep-pricing" style={inlineLinkOnLight}>Amazon FBA prep pricing</Link> or{" "}
                <Link to="/contact-fulfillment-services" style={inlineLinkOnLight}>speak to our team</Link> directly.
              </p>

              <form onSubmit={handleWholesaleFormSubmit} noValidate>
                {submitError ? (
                  <p
                    style={{
                      fontFamily: FONT_STACK,
                      fontSize: "0.85rem",
                      color: "#dc2626",
                      marginBottom: 16,
                      lineHeight: 1.5,
                    }}
                    role="alert"
                  >
                    {submitError}
                  </p>
                ) : null}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontFamily: FONT_STACK, fontSize: "0.75rem", color: TEXT_MID, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 8 }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    autoComplete="name"
                    required
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (submitError) setSubmitError("");
                    }}
                    style={{
                      width: "100%",
                      background: PAGE_ALT,
                      border: `1px solid ${PAGE_BORDER}`,
                      borderRadius: 8,
                      padding: "12px 16px",
                      color: TEXT_DARK,
                      fontFamily: FONT_STACK,
                      fontSize: "0.9rem",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = ACCENT)}
                    onBlur={(e) => (e.target.style.borderColor = PAGE_BORDER)}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontFamily: FONT_STACK, fontSize: "0.75rem", color: TEXT_MID, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 8 }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (submitError) setSubmitError("");
                    }}
                    style={{
                      width: "100%",
                      background: PAGE_ALT,
                      border: `1px solid ${PAGE_BORDER}`,
                      borderRadius: 8,
                      padding: "12px 16px",
                      color: TEXT_DARK,
                      fontFamily: FONT_STACK,
                      fontSize: "0.9rem",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = ACCENT)}
                    onBlur={(e) => (e.target.style.borderColor = PAGE_BORDER)}
                  />
                </div>

                <div style={{ marginBottom: 32 }}>
                  <label style={{ display: "block", fontFamily: FONT_STACK, fontSize: "0.75rem", color: TEXT_MID, letterSpacing: "0.1em", fontWeight: 600, marginBottom: 8 }}>
                    Monthly Volume
                  </label>
                  <select
                    name="monthlyVolume"
                    required
                    value={formData.volume}
                    onChange={(e) => {
                      setFormData({ ...formData, volume: e.target.value });
                      if (submitError) setSubmitError("");
                    }}
                    style={{
                      width: "100%",
                      background: PAGE_ALT,
                      border: `1px solid ${PAGE_BORDER}`,
                      borderRadius: 8,
                      padding: "12px 16px",
                      color: formData.volume ? TEXT_DARK : TEXT_SOFT,
                      fontFamily: FONT_STACK,
                      fontSize: "0.9rem",
                      outline: "none",
                      cursor: "pointer",
                      appearance: "none",
                      WebkitAppearance: "none",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = ACCENT)}
                    onBlur={(e) => (e.target.style.borderColor = PAGE_BORDER)}
                  >
                    <option value="" disabled>
                      Select your monthly volume
                    </option>
                    <option value="500-2000">500 – 2,000 units/month</option>
                    <option value="2000-6000">2,000 – 6,000 units/month</option>
                    <option value="6000-15000">6,000 – 15,000 units/month</option>
                    <option value="15000+">15,000+ units/month</option>
                  </select>
                </div>

                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    background: ACCENT,
                    color: "#ffffff",
                    border: "none",
                    padding: "14px",
                    borderRadius: 8,
                    fontSize: "0.95rem",
                    fontFamily: FONT_STACK,
                    fontWeight: 700,
                    cursor: isSubmitting ? "wait" : "pointer",
                    letterSpacing: "0.05em",
                    transition: "background 0.2s",
                    opacity: isSubmitting ? 0.8 : 1,
                  }}
                >
                  {isSubmitting ? "Sending…" : "Send My Rate Request →"}
                </button>
                <p style={{ fontFamily: FONT_STACK, fontSize: "0.72rem", color: TEXT_SOFT, textAlign: "center", marginTop: 14 }}>
                  No spam. Response within 2 hours on business days.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
      </div>
    </div>
  );
}
